import { QueryClient, type QueryClientConfig } from "@tanstack/react-query";
import { ApiError, AuthError } from "./api-client";
import { queryKeys } from "./query-keys";

/**
 * Shared QueryClient configuration for the admin SPA.
 *
 * Conventions:
 *   - staleTime: 30 seconds. Data <30s old is considered fresh and
 *     doesn't refetch on window focus — enough to suppress the
 *     "every tab switch re-renders" feel while still looking live.
 *   - retry: no retry on 4xx (client errors). Retry 2× on 5xx.
 *   - refetchOnWindowFocus: true — admin consoles benefit from
 *     up-to-date data when the operator returns to the tab.
 *   - 401 handling (session expiry): throwOnError promotes AuthError
 *     to a render-time throw so the router's errorComponent catches
 *     it and renders SignedOutScreen. Every other error still flows
 *     through `{ isError, error }` in the hook result so pages can
 *     render their usual banner for 5xx / 403 / network errors.
 *     This is what makes the session-expired UX work uniformly on
 *     every page — without this, each page's local isError branch
 *     would render a raw "Bearer token is invalid or expired" banner
 *     instead of the purpose-built signed-out screen.
 */
const throwOn401 = (error: Error): boolean => error instanceof AuthError;

const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      throwOnError: throwOn401,
    },
    mutations: {
      // Mutations don't retry — we want the user to see the failure
      // and act. 401 on a mutation means the session died mid-edit;
      // promote it so the signed-out screen fires instead of a
      // silent toast that leaves the operator confused about what
      // happened to their unsaved work.
      retry: false,
      throwOnError: throwOn401,
    },
  },
};

/**
 * The query-key HEADS the non-human census is a projection of.
 *
 * Every entry is the literal first element of a real key, verified against the
 * code that builds it — NOT inferred from the feature's folder name, which is how
 * the first version of this list went wrong. It named "compliance" and
 * "directory" and stopped there, so the census was invalidated for 3 of the 6
 * surfaces that change it, and the one the docstring below cites as its
 * motivating example — revoking an auditor token — was among the misses.
 *
 * Two of these heads are inline literals rather than queryKeys entries, which is
 * exactly why guessing failed; census-invalidation.test.ts pins all six against
 * the code that builds them so this cannot drift silently again.
 */
const CENSUS_SOURCE_PREFIXES: readonly string[] = [
  // agents: queryKeys.agents.all
  "agents",
  // automation accounts: queryKeys.automationAccounts.all(scope)
  "automationAccounts",
  // AD-inbound directories: queryKeys.directory.adInbound.*
  "directory",
  // SCIM tokens: queryKeys.scimTokens.all(tenantID) / .allTokens(tenantID)
  "scim",
  // auditor tokens: queryKeys.auditorTokens.all(tenantID)
  "compliance-v2",
  // trusted issuers: queryKeys.workloadIssuers.all. Not a census SOURCE, but
  // classifying or disabling an issuer changes the CATEGORY and the STATE of
  // every workload federated from it — one click moving thousands of rows.
  "workload-issuers",
];

/**
 * Keep the census fresh when anything it counts changes.
 *
 * WHY THIS IS CENTRAL RATHER THAN PER-MUTATION. The census is derived from five
 * other features' tables, and both queries.ts and query-keys.ts claimed the
 * surfaces that mutate those tables invalidate it. None of them did — the only
 * invalidator was the issuer-category mutation — so revoking an auditor token
 * and returning to the census within the stale window still read "N can act
 * right now", with the revoked token counted among them.
 *
 * The obvious fix is to add `nhiCensus.all` to each mutation's onSuccess. Those
 * four features hold 49 invalidation sites between them, most of which touch
 * nothing the census counts, and every future mutation would have to remember —
 * which is exactly the drift that produced this finding. So the coupling is
 * declared ONCE, here, where it can be read in full: when a census SOURCE is
 * invalidated, the projection over it is stale too.
 *
 * Deliberately keyed on invalidation rather than on mutation success: a source
 * invalidated for ANY reason (a mutation, a manual refresh, a websocket-driven
 * refresh later) means the same thing for the projection.
 *
 * ONE PRECONDITION, stated because it is not obvious: this observes a QUERY being
 * invalidated, so it fires only when a query under that key is actually in the
 * cache. That is the real case — an operator mutating a list is looking at it —
 * and the fallback when it is not cached is the ordinary one: the census refetches
 * when its own page is next mounted. What this closes is the window where the
 * census is ALREADY OPEN in another tab or behind the operator, which is where a
 * revoked credential kept being counted as able to act.
 *
 * The census's own invalidations do not re-enter — its key is not a source
 * prefix — so there is no loop.
 */
export function wireCensusInvalidation(client: QueryClient): () => void {
  return client.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "invalidate") return;
    const head = event.query.queryKey[0];
    if (typeof head !== "string" || !CENSUS_SOURCE_PREFIXES.includes(head)) return;
    void client.invalidateQueries({ queryKey: queryKeys.nhiCensus.all });
  });
}

export function createQueryClient(): QueryClient {
  const client = new QueryClient(queryClientConfig);
  wireCensusInvalidation(client);
  return client;
}
